import {Component, computed, Inject, OnInit, signal} from '@angular/core';
import {MatSelect} from '@angular/material/select';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ObservationReviewDTO} from '../../../models/claim-review/ObservationReviewDTO';
import {ObservationTypeDTO} from '../../../models/claim-review/ObservationyTypeDTO';
import {FormArray, FormBuilder, FormControl, FormGroup, AbstractControl} from '@angular/forms';
import {CreateNameValueDTO} from '../../../../common/models/NameValueDTO';

enum InputType {
  Text = 'text',
  Number = 'number',
  Percent = 'percent',
  Decimal = 'decimal',
  Integer = 'integer',
  PositiveDecimal = 'positive decimal',
  PositiveInteger = 'positive integer',
}

@Component({
  selector: 'app-add-observation-dialog',
  templateUrl: './add-observation-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss', './add-observation-dialog.component.scss']
})
export class AddObservationDialogComponent implements OnInit {

  observations = signal<ObservationReviewDTO[]>([]);
  observationTypes = signal<ObservationTypeDTO[]>([]);
  observationTypeFormGroup: FormGroup;

  familyOfBenefits = computed(() => {
    const types = this.observationTypes().filter(o => o.benefitFamily != null);
    const families = Array.from(new Set(types.map(o => o.benefitFamily.name))).sort((a, b) => a.localeCompare(b));
    return [...families, this.NO_ASSIGNED_FAMILY_OF_BENEFIT];
  });

  selectedFamilyOfBenefits = signal<string[]>([]);
  observationFilterControl = new FormControl('');
  private NO_ASSIGNED_FAMILY_OF_BENEFIT = 'General';
  private SELECT_ALL_CONTROL_IDENTIFIER = 'Select All';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AddObservationDialogComponent>
  ) {
    this.observations.set(data.observations || []);
    this.observationTypes.set(data.observationTypes || []);
    this.observationTypeFormGroup = this.formBuilder.group({
      observationTypeFormArray: this.formBuilder.array([])
    });
  }

  get observationTypeFormArray(): FormArray {
    return this.observationTypeFormGroup ? this.observationTypeFormGroup.get('observationTypeFormArray') as FormArray : null;
  }

  ngOnInit(): void {
    this.populateFormArray();
    this.selectedFamilyOfBenefits.set(this.getDefaultFamilyOfBenefits());
  }

  getInputType(valueType: string): string {
    switch (valueType) {
      case InputType.Percent:
      case InputType.Decimal:
      case InputType.Integer:
      case InputType.PositiveDecimal:
      case InputType.PositiveInteger:
        return InputType.Number;
      default:
        return InputType.Text;
    }
  }

  getInputPattern(valueType: string): string {
    switch (valueType) {
      case InputType.Integer:
        return '^-?[0-9]*$'; // Allows both positive and negative integers
      case InputType.Decimal:
        return '^-?[0-9]*(\.[0-9]+)?$'; // Allows both positive and negative decimals
      case InputType.PositiveInteger:
        return '^(0|[1-9][0-9]*)$'; // Positive integers including 0
      case InputType.PositiveDecimal:
        return '^(0|[1-9][0-9]*)(\.[0-9]+)?$'; // Positive decimals including 0
      case InputType.Percent:
        return '^(100(\\.0+)?|[0-9]{1,2}(\\.[0-9]+)?)$'; // Percentages from 0 to 100, including decimals
      default:
        return '.*';
    }
  }

  isValueBlank(value: string): boolean {
    return value == null || value.trim() === '';
  }

  onClickCancel(): void {
    this.dialogRef.close();
  }

  onClickSave(): void {
    const observationReviewDTOs: ObservationReviewDTO[] = [];
    if (this.observationTypeFormArray != null) {
      this.observationTypeFormArray.controls
        .forEach(control => {
          if (!this.isValueBlank(control.get('value').value)) {
            const observationType: ObservationTypeDTO = this.observationTypes().find(observationTypeInner => observationTypeInner.name === control.get('name').value);
            const observationReviewDTO: ObservationReviewDTO = new ObservationReviewDTO();
            observationReviewDTO.observationType = CreateNameValueDTO(observationType.observationTypeId, observationType.name);
            observationReviewDTO.valueType = CreateNameValueDTO(observationType.valueType?.value, observationType.valueType?.name);
            observationReviewDTO.observationValue = control.get('value').value;

            if (control.get('showEffectiveDate').value) {
              observationReviewDTO.effectiveDate = control.get('effectiveDate')?.value;
            }
            observationReviewDTOs.push(observationReviewDTO);
          }
        });
    }
    this.dialogRef.close(observationReviewDTOs);
  }

  clearSelection(select: MatSelect): void {
    select.value = null;
  }

  getControlsByFamilyOfBenefitGrouping(familyOfBenefit: string): AbstractControl[] {
    if (familyOfBenefit == null || familyOfBenefit === this.NO_ASSIGNED_FAMILY_OF_BENEFIT) {
      return this.observationTypeFormArray.controls.filter(control => control.value?.benefitFamily == null || control.value?.benefitFamily?.name == 'Unknown');
    }
    return this.observationTypeFormArray.controls.filter(control => control.value?.benefitFamily?.name == familyOfBenefit);
  }

  getSelectedFamilyOfBenefits(): string[] {
    return this.selectedFamilyOfBenefits().filter(familyOfBenefit => familyOfBenefit !== this.SELECT_ALL_CONTROL_IDENTIFIER);
  }

  onSelectedFamilyOfBenefitsChanged($event): void {
    this.selectedFamilyOfBenefits.set(Object.keys($event));
  }

  clearObservationFilter() : void {
    this.observationFilterControl.setValue('');
  }

  filterOnObservationValuesByFilter(filter: string): AbstractControl[] {
    const caseInsensitiveFilterValue: string = filter.toLowerCase();
    return this.observationTypeFormArray.controls.filter(control => control.value?.name.toLowerCase().includes(caseInsensitiveFilterValue));
  }

  private getDefaultFamilyOfBenefits(): string[] {
    return [...(this.familyOfBenefits() ?? []), this.SELECT_ALL_CONTROL_IDENTIFIER];
  }

  private populateFormArray(): void {
    this.observationTypes()
      .forEach((observationType: ObservationTypeDTO) => {
        if (observationType.name != 'Unknown') {
          const observation: ObservationReviewDTO = this.observations().find(observationInner => observationInner.observationType.name === observationType.name);
          const group: FormGroup = this.formBuilder.group({
            name: new FormControl(observationType.name),
            value: new FormControl(observation != null ? observation.observationValue : null),
            valueType: new FormControl(observation != null && observation.valueType != null && observation.valueType.value != 1 ? observation.valueType.name : observationType.valueType?.name),
            options: new FormControl(observationType.options),
            effectiveDate: new FormControl(observation?.effectiveDate ? new Date(observation.effectiveDate) : null),
            showEffectiveDate: new FormControl(observationType.showEffectiveDate),
            benefitFamily: new FormControl(observationType.benefitFamily)
          });
          this.observationTypeFormArray.push(group);
        }
      });
  }
}
